import { NoticeBannerConfig } from '@superfunnel/types';
import { classNames } from '@superfunnel/utils';
import { InfoCircle, WarningTriangle, Xmark } from 'iconoir-react';
import { useState } from 'react';

interface NoticeBannerProps {
  config: NoticeBannerConfig;
}

const variantStyles = {
  info: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-800',
    icon: InfoCircle,
    iconColor: 'text-blue-500',
  },
  warning: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-800',
    icon: WarningTriangle,
    iconColor: 'text-amber-500',
  },
  error: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-800',
    icon: WarningTriangle,
    iconColor: 'text-red-500',
  },
};

/**
 * A dismissible notice banner displayed at the top of the dashboard.
 * Controlled by Firestore system config.
 */
export function NoticeBanner({ config }: NoticeBannerProps) {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  const variant = config.variant ?? 'info';
  const dismissible = config.dismissible ?? true;
  const styles = variantStyles[variant];
  const Icon = styles.icon;

  return (
    <div
      className={classNames(
        'flex shrink-0 items-center justify-center gap-3 border-b px-4 py-2',
        styles.bg,
        styles.border,
      )}
    >
      <Icon className={classNames('h-5 w-5 flex-shrink-0', styles.iconColor)} />
      <p className={classNames('text-sm', styles.text)}>{config.message}</p>
      {dismissible && (
        <button
          onClick={() => setDismissed(true)}
          className={classNames(
            'ml-auto rounded-md p-1 hover:bg-black/5',
            styles.text,
          )}
          aria-label="Dismiss"
        >
          <Xmark className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
